const global = window.wrike;

/**
 * Creates a new ContactSales instance
 *
 * @class
 * @classdesc Handle for ContactSales forms
 *
 * @param {object}    options                        - Options for ContactSales.
 * @param {!boolean}  options.subType                - sub_type of ContactSales request.
 * @param {!string}   options.clientId               - Chunk of request header `Wrike-Client-Id`.
 * @param {boolean=}  options.isInit                 - Conditions of ContactSales initialing.
 * @param {jQuery=}   options.$handler               - Element
 * @param {!jQuery}   options.$form                  - Element of ContactSales Form.
 * @param {jQuery=}   options.$title                 - Element for shaking when an incorrectly filled form is sent.
 * @param {jQuery=}   options.$successMessage        - Element to show when form submit success fired.
 * @param {jQuery=}   options.$closeHandler          - Element to close ContactSales success message.
 * @param {string=}   options.classCloseColorInvert  - Html class that will be added to close handler when inline success message is opened.
 * @param {function=} options.onOpen                 - Event called after click on $handler.
 * @param {function=} options.onSuccess              - Event called after request success.
 *
 */

class ContactSales {
    constructor(options) {
        this.options = options;
        this.formData = {};
        this.source = '';
        this.plan = '';
    }

    init() {
        const { $handler, $form, onOpen, $title } = this.options;
        const contactSales = this;
        const shakingTimeout = 400;

        if ($handler && $handler.length) {
            $handler.on('click', (event) => {
                event.preventDefault();
                this.source = $(event.currentTarget).data('source') || '';
                this.plan = $(event.currentTarget).data('plan') || '';

                if (typeof onOpen === 'function') {
                    onOpen.call(this, this);
                }
            });
        }

        this.initCheckboxes();

        $form.on('submit', (event) => {
            event.preventDefault();

            if ($form.attr('submitting')) {
                return;
            }

            if ($form.wgInstance().validateForm()) {
                this.setFormData();
                let formData = contactSales.getSalesData($form);

                global.disableForm($form);
                contactSales.request(formData);
            } else {
                global.enableForm($form);
                $form
                    .find('.wg-input--invalid')
                    .eq(0)
                    .focus();

                if ($title && $title.length) {
                    global.shake($title, shakingTimeout);
                }
            }
        });
    }

    setFormData() {
        const $form = this.options.$form;
        const serializedForm = $form.serializeArray().concat(this.getCheckboxesData());

        this.formData = global.fromPairs(serializedForm);
    }

    getSalesData() {
        this.setFormData();

        const details = {
            job_role: this.formData.job_role || '',
            job_level: this.formData.job_level || ''
        };

        if ('question' in this.formData) {
            details.question = this.formData.question;
        }

        if ('geo' in window && 'getCountryCode' in window.geo) {
            this.formData.countryCode = window.geo.getCountryCode();
        }

        /**
         * See the backend documentation for details.
         * @see {@link https://alpha.wrke.io/backend-docs/allwebmethods.html#/ui/create_email_sales_event |Backend handler}
         */
        return {
            type: 'CallRequest',
            sub_type: this.options.subType,
            name: this.formData.name,
            email: this.formData.email,
            phone: `${this.formData.code} ${this.formData.phone}`,
            job_role: this.formData.job_role,
            countryCode: this.formData.countryCode,
            source: this.source,
            details: JSON.stringify(details)
        };
    }

    request(formData) {
        const { $form, clientId } = this.options;
        $.ajax({
            type: 'POST',
            url: 'https://' + window.location.host + '/ui/create_email_sales_event',
            headers: {
                'Wrike-Client-Id': `${clientId}-${Date.now()}${global.getRandomInt(0, 9)}`
            },
            data: formData,
            dataType: 'JSON'
        })
            .then(() => {
                $form.trigger('success.contact-sales');
                this.sendMarketoForm();
            })
            .fail(() => {
                $form.trigger('error.contact-sales');
                global.enableForm($form);

                throw new Error(clientId + ' error');
            });
    }

    requestSuccess() {
        const { $form, onSuccess, $successMessage, $closeHandler, classCloseColorInvert } = this.options;
        const isInlineSuccessMessage = 'resend' in $form.data() === false;
        const $modal = $form.closest('.wg-modal');
        const $wgHide = $modal.find('[data-wg-hide]');

        if (typeof onSuccess === 'function') {
            onSuccess.call(this, this);
        }

        if (isInlineSuccessMessage) {
            $form.hide();

            if ($successMessage) {
                $successMessage.show();
            }

            if (!$closeHandler || !$wgHide) {
                return;
            }

            if (classCloseColorInvert) {
                $closeHandler.addClass(classCloseColorInvert);
            }

            $wgHide.one('click', (event) => {
                event.stopPropagation();
                event.preventDefault();

                if ($modal && $modal.length) {
                    $modal.wgInstance().hide();
                }

                if ($successMessage) {
                    $successMessage.hide();
                }

                $form.show();
                $form.wgInstance().resetForm();
                global.resetForm($form);

                if (classCloseColorInvert) {
                    $closeHandler.removeClass(classCloseColorInvert);
                }
            });
        } else {
            window.location.href = 'https://' + window.location.host + global.getPathLocale() + $form.data('resend');
        }
    }

    sendMarketoForm() {
        const userData = this.prepareDataForMarketo();

        const techcallmeValue = this.getValueFromFormData({
            formData: this.formData,
            fieldName: 'TechCallMe',
            nonGdprUserValue: 'true'
        });

        const techemailmValue = this.getValueFromFormData({
            formData: this.formData,
            fieldName: 'TechEmailMe',
            nonGdprUserValue: 'true'
        });

        if (techcallmeValue) {
            userData.techcallme = techcallmeValue;
        }

        if (techemailmValue) {
            userData.techemailme = techemailmValue;
        }

        const contactSalesMarketo = new WG.Marketo(this.options.marketoId, userData);
        contactSalesMarketo.then(() => {
            this.setCookiesCheckboxes(this.formData, 'TechCallMe');
            this.setCookiesCheckboxes(this.formData, 'TechEmailMe');
            this.requestSuccess();
        });
    }

    prepareDataForMarketo() {
        const cookie = window.WG.utils.getCookie('_ga');

        if ('geo' in window && 'getCountryCode' in window.geo) {
            this.formData.countryCode = window.geo.getCountryCode();
        }

        const clientName = WG.Marketo.prepareFirstAndLastName(this.formData.name);

        return {
            FirstName: clientName.firstName,
            LastName: clientName.lastName,
            Email: this.formData.email,
            Phone: `${this.formData.code} ${this.formData.phone}`,
            CountryCode: this.formData.countryCode,
            job_bucket_role: this.formData.job_role,
            job_bucket_level: this.formData.job_level,
            Marketo_Marketing_Notes__c: this.formData.question,
            ga_cookies: cookie,
            form_type: this.options.marketoFormType
        };
    }

    getCheckboxesData() {
        let moreInfo = [];

        this.options.$form.find('input[type=checkbox]').each(function() {
            if (!this.checked) {
                moreInfo.push({
                    name: this.name,
                    value: 'false'
                });
            } else {
                moreInfo.push({
                    name: this.name,
                    value: 'true'
                });
            }
        });

        return moreInfo;
    }

    getValueFromFormData({ formData, fieldName, nonGdprUserValue, defaultValue = '' }) {
        if (window.WG.utils.isEuVisitor()) {
            if (formData.hasOwnProperty(fieldName)) {
                return formData[fieldName];
            } else {
                return defaultValue;
            }
        } else {
            return nonGdprUserValue;
        }
    }

    setCookiesCheckboxes(formData, type) {
        if (
            formData.hasOwnProperty(type) &&
            window.WG.utils.isEuVisitor() &&
            !window.WG.utils.isConfirmCheckbox(type)
        ) {
            if (formData[type] === 'true') {
                window.WG.utils.saveConfirmCheckboxAnswer(type, 'true');
            } else {
                window.WG.utils.saveConfirmCheckboxAnswer(type, 'false');
            }
        }
    }

    initCheckboxes() {
        this.options.$form.find('.form-contact__checkbox').each(function() {
            if (
                !window.WG.utils.isEuVisitor() ||
                (this.dataset.type === 'TechEmailMe' &&
                    window.WG.utils.getCookie('wrike_tech_email_me_confirmed') === 'true') ||
                (this.dataset.type === 'TechCallMe' &&
                    window.WG.utils.getCookie('wrike_tech_call_me_confirmed') === 'true')
            ) {
                this.remove();
            }
        });
    }
}

window.ContactSales = ContactSales;



// WEBPACK FOOTER //
// ./assets/js/common/modules/contact-sales.js